import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useUsageCompilationFormSchema } from "./useUsageCompilationFormSchema";
import { useUsageCompilationCascade } from "./useUsageCompilationCascade";
import { useUsageCompilationFormMutations } from "./useUsageCompilationFormMutations";
import {
  flattenSelectableFields,
  formatUsageCompilationSuccessMessage,
  mapUsageCompilationRowForEdit,
} from "../utils/usageCompilationFormRules";

/**
 * State Management Layer - orchestrating hook, mirrors
 * _features/usageSources/modals/UsageSourceFormModal/hooks/
 * useUsageSourceFormState.js's shape (dynamic-form engine + close-
 * confirmation dismiss guard + submit orchestration), extended to wire
 * the cascading-dependency hook.
 *
 * Uses a "latest ref" for the schema's Reset-button callback
 * (`handleChangeRef`, same established pattern as `_features/auth`'s
 * `handleEnterRef`) because the schema (built by
 * useUsageCompilationFormSchema, which needs a stable onResetCalculation
 * callback) has to exist *before* useDynamicForm does - so the callback
 * can't close over `dynamicForm.handleChange` directly at schema-build
 * time. The ref is reassigned every render and only read when the button
 * is actually clicked, by which point it's always current.
 */
export function useUsageCompilationFormState({ mode, rowData, isOpen, onClose, onSuccess, refetchFilters }) {
  const { showToast } = useToast();
  const { submitUsageCompilation, isSubmitting } = useUsageCompilationFormMutations();

  const cascade = useUsageCompilationCascade({ mode, rowData });

  const handleChangeRef = useRef(() => {});
  const resetCalculation = useCallback(() => cascade.resetCalculation(handleChangeRef.current), [cascade]);

  const rawSchema = useUsageCompilationFormSchema({
    sourceData: cascade.sourceData,
    isCheckingSource: cascade.isCheckingSource,
    sourceInspectionError: cascade.sourceInspectionError,
    selectedTable: cascade.selectedTable,
    selectedSheet: cascade.selectedSheet,
    selectedUsageSource: cascade.selectedUsageSource,
    onResetCalculation: resetCalculation,
  });

  const editRowData = useMemo(
    () => (mode === "edit" ? mapUsageCompilationRowForEdit(rowData) : rowData),
    [mode, rowData]
  );
  const dynamicForm = useDynamicForm(rawSchema, { mode, rowData: editRowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { isDirty },
  } = dynamicForm;

  handleChangeRef.current = dynamicForm.handleChange;

  const handleChange = useCallback(
    (name, value) => {
      dynamicForm.handleChange(name, value);

      if (name === "usageSource") cascade.handleUsageSourceChange(value, dynamicForm.handleChange);
      else if (name === "table") cascade.handleTableChange(value, dynamicForm.handleChange);
      else if (name === "sheet") cascade.handleSheetChange(value, dynamicForm.handleChange);
    },
    [dynamicForm, cascade]
  );

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setShowCloseConfirmation(false);
    onClose();
  }

  function attemptClose() {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    try {
      const dataForAPI = flattenSelectableFields(validData);
      const response = await submitUsageCompilation({ mode, validData: dataForAPI, rowData });

      showToast(formatUsageCompilationSuccessMessage(validData, mode), "success");
      onSuccess?.(response);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      showToast(error?.data?.message || "Something went wrong. Please try again.", "error");
    }
  });

  return {
    ...dynamicForm,
    handleChange,
    schema: rawSchema,
    showCloseConfirmation,
    hasUnsavedChanges: isDirty,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
  };
}
